import { useMemo } from 'react';

/**
 * CRPD-7209 de-mock — the cost accessor layer, now reading the real generated
 * Control fields (CRPD-7222 schema). This replaces the deleted `src/api/costMocks/`
 * layer and stays the single seam between query data and the cost surfaces.
 *
 * Note: the BE collapsed the two spec-§3.1 notes to a single `costNote` ↔ `cost_note`
 * (Tim's call, 2026-07-23) — the one-vs-two-notes question is settled; the FE's single
 * "Cost Note" now maps 1:1.
 */

/** Cost fields as consumed by every display surface. */
export interface ControlCostFields {
  /** Evaluated (when annualCostFormula is set) or literal annual cost. */
  annualCost: number | null;
  annualCostFormula: string | null;
  /** Evaluated (when implementationCostFormula is set) or literal implementation cost. */
  implementationCost: number | null;
  implementationCostFormula: string | null;
  costNote: string | null;
}

/** Stored (input) shape the edit modal round-trips — same fields, literal semantics. */
export type ControlCostInputFields = ControlCostFields;

export interface FrameworkControlCostFields {
  annualCostFormula: string | null;
  implementationCostFormula: string | null;
  costNote: string | null;
}

/** Structural slice of a generated Control any cost surface needs. */
export interface ControlCostSource {
  id: string;
  annualCost?: number | null;
  annualCostFormula?: string | null;
  costNote?: string | null;
  implementationCost?: number | null;
  implementationCostFormula?: string | null;
}

export const toControlCostFields = (control: ControlCostSource): ControlCostFields => ({
  annualCost: control.annualCost ?? null,
  annualCostFormula: control.annualCostFormula ?? null,
  implementationCost: control.implementationCost ?? null,
  implementationCostFormula: control.implementationCostFormula ?? null,
  costNote: control.costNote ?? null,
});

export const useControlCostFields = (control: ControlCostSource | null | undefined): ControlCostFields | null => {
  return useMemo(() => (control ? toControlCostFields(control) : null), [control]);
};

/** Bulk variant for tables — one call at page level, cells receive fields via props. */
export const useControlsCostFields = (controls: ControlCostSource[]): Map<string, ControlCostFields> => {
  return useMemo(() => {
    const result = new Map<string, ControlCostFields>();
    controls.forEach((control) => result.set(control.id, toControlCostFields(control)));
    return result;
  }, [controls]);
};

/** Structural slice of a generated FrameworkControl carrying the template cost columns (CRPD-7222 WS-1). */
export interface FrameworkControlCostSource {
  id: string;
  annualCostFormula?: string | null;
  implementationCostFormula?: string | null;
  costNote?: string | null;
}

// `id` is only needed by the bulk variants (they key by it) — the single-item
// accessors accept id-less structural rows (e.g. TableSheetControlLibrary's data).
export const toFrameworkControlCostFields = (fc: Omit<FrameworkControlCostSource, 'id'>): FrameworkControlCostFields => ({
  annualCostFormula: fc.annualCostFormula ?? null,
  implementationCostFormula: fc.implementationCostFormula ?? null,
  costNote: fc.costNote ?? null,
});

/**
 * Does this framework-control template carry ANY cost content?
 *
 * CRPD-7345 — the rule behind "a template with no cost data hides its cost fields rather
 * than showing them blank" (epic CRPD-7138 AC1 — first applied to `TableSheetControlLibrary`
 * in CRPD-7209, extended to `ControlFrameworkDetails` in CRPD-7287, and reaching this page
 * only now). The block hides only when ALL THREE fields are empty: if any one is
 * populated the surface keeps the whole block so a populated field is never orphaned.
 *
 * Whitespace counts as empty because `resolveCostDisplay` (util.ts) renders a
 * blank-but-present FORMULA in the `blank` ("No data") state — so a bare truthiness check
 * would leave the section visible showing nothing but "No data". That trim is scoped to
 * this predicate: a cost NOTE is still rendered behind a plain truthiness check at its call
 * site, so a whitespace-only note sitting next to a populated formula prints the whitespace
 * rather than "No data". Unreachable from the API today: the only writer of these three
 * `framework_controls` columns is `ContentImportService::upsertFrameworkControlsForFramework`,
 * and every value it stores has passed through that service's `nullableString`, which trims
 * and turns `''` into null. Framework-control templates only — the identically named columns
 * on a workspace `Control` are a different table with a different writer
 * (`ControlResolver::nullableInput`, which does NOT trim), so a whitespace-only note IS
 * storable there. Do not generalise this predicate to `ControlCostFields` without revisiting
 * that.
 */
export const hasFrameworkControlCostContent = (fields: FrameworkControlCostFields | null | undefined): boolean =>
  !!fields &&
  [fields.annualCostFormula, fields.implementationCostFormula, fields.costNote].some(
    (value) => value != null && String(value).trim() !== '',
  );

/** Template cost fields for one framework control — sourced from the row's real generated fields. */
export const useFrameworkControlCostFields = (
  frameworkControl: Omit<FrameworkControlCostSource, 'id'> | null | undefined,
): FrameworkControlCostFields | null => {
  return useMemo(() => (frameworkControl ? toFrameworkControlCostFields(frameworkControl) : null), [frameworkControl]);
};

/** Bulk variant for library tables — one call at page level, keyed by framework-control id. */
export const useFrameworkControlsCostFields = (
  frameworkControls: FrameworkControlCostSource[],
): Map<string, FrameworkControlCostFields> => {
  return useMemo(() => {
    const result = new Map<string, FrameworkControlCostFields>();
    frameworkControls.forEach((fc) => result.set(fc.id, toFrameworkControlCostFields(fc)));
    return result;
  }, [frameworkControls]);
};
